import { collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface LandingPageVisit {
  timestamp: string;
  referrer: string | null;
  userAgent: string;
  language: string;
  screenWidth?: number;
  screenHeight?: number;
  isExternal: boolean;
  source?: string; // 'social_media', 'direct', 'external_link', etc.
}

const STATISTICS_COLLECTION = 'Statistics';
const TRACKING_FLAG_KEY = 'morafaa_landing_page_tracked';

/**
 * Track a landing page visit from external sources
 * This function can be called without authentication
 * All errors are handled silently to ensure no impact on user experience
 * Uses sessionStorage to prevent duplicate tracking in the same session
 */
export const trackLandingPageVisit = async (): Promise<void> => {
  // Use a separate try-catch wrapper to ensure function never throws
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return; // Silently exit if not in browser
    }

    // Check if we've already tracked this visit in this session
    // This prevents duplicate tracking from React StrictMode or double mounting
    try {
      const alreadyTracked = sessionStorage.getItem(TRACKING_FLAG_KEY);
      if (alreadyTracked === 'true') {
        // Already tracked in this session, skip
        if (process.env.NODE_ENV === 'development') {
          console.log('⏭️ Landing page visit already tracked in this session, skipping');
        }
        return;
      }
    } catch (storageError) {
      // If sessionStorage is not available (e.g., private browsing), continue anyway
      // This is a best-effort deduplication mechanism
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ sessionStorage not available, continuing without deduplication');
      }
    }

    // Check if Firestore is available
    if (!db) {
      return; // Silently exit if Firestore is not initialized
    }

    let referrer: string | null = null;
    let currentOrigin: string = '';
    let isExternal: boolean = false;
    let source: string = 'direct';

    try {
      // Safely get referrer information
      referrer = document.referrer || null;
      currentOrigin = window.location.origin;
      
      // Determine if the visit is from an external source
      isExternal = referrer !== null && !referrer.startsWith(currentOrigin);
      
      // Detect source type with safe URL parsing
      if (isExternal && referrer) {
        try {
          const referrerHost = new URL(referrer).hostname.toLowerCase();
          
          // Social media sources
          if (referrerHost.includes('facebook.com') || referrerHost.includes('fb.com')) {
            source = 'social_media_facebook';
          } else if (referrerHost.includes('instagram.com')) {
            source = 'social_media_instagram';
          } else if (referrerHost.includes('twitter.com') || referrerHost.includes('x.com')) {
            source = 'social_media_twitter';
          } else if (referrerHost.includes('linkedin.com')) {
            source = 'social_media_linkedin';
          } else if (referrerHost.includes('whatsapp.com') || referrerHost.includes('wa.me')) {
            source = 'social_media_whatsapp';
          } else if (referrerHost.includes('telegram.org') || referrerHost.includes('t.me')) {
            source = 'social_media_telegram';
          } else {
            source = 'external_link';
          }
        } catch (urlError) {
          // If URL parsing fails, treat as external link
          source = 'external_link';
        }
      } else if (!referrer) {
        source = 'direct';
      }
    } catch (dataError) {
      // If data collection fails, use defaults
      source = 'direct';
      isExternal = false;
    }
    
    // Safely prepare visit data with fallbacks
    let visitData: LandingPageVisit;
    try {
      visitData = {
        timestamp: new Date().toISOString(),
        referrer: referrer,
        userAgent: typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : 'unknown',
        language: typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en',
        screenWidth: typeof window !== 'undefined' && window.screen?.width ? window.screen.width : undefined,
        screenHeight: typeof window !== 'undefined' && window.screen?.height ? window.screen.height : undefined,
        isExternal: isExternal,
        source: source
      };
    } catch (dataPrepError) {
      // If data preparation fails, use minimal data
      visitData = {
        timestamp: new Date().toISOString(),
        referrer: null,
        userAgent: 'unknown',
        language: 'en',
        isExternal: false,
        source: 'direct'
      };
    }
    
    // Add to Firestore with timeout protection
    try {
      // Use Promise.race to add a timeout (5 seconds max)
      const firestorePromise = addDoc(collection(db, STATISTICS_COLLECTION), visitData);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Firestore operation timeout')), 5000)
      );
      
      await Promise.race([firestorePromise, timeoutPromise]);
      
      // Mark as tracked in sessionStorage to prevent duplicates
      try {
        sessionStorage.setItem(TRACKING_FLAG_KEY, 'true');
      } catch (storageError) {
        // Ignore storage errors - this is just for deduplication
      }
      
      // Only log success in development
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Landing page visit tracked:', {
          source,
          isExternal,
          referrer: referrer || 'direct'
        });
      }
    } catch (firestoreError: any) {
      // Handle specific Firestore errors silently
      const errorCode = firestoreError?.code || 'unknown';
      const errorMessage = firestoreError?.message || 'Unknown error';
      
      // Only log in development, never show to user
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Statistics tracking failed (silent):', {
          code: errorCode,
          message: errorMessage
        });
      }
      
      // Silently return - don't throw or affect user experience
      return;
    }
  } catch (error: any) {
    // Ultimate safety net - catch any unexpected errors
    // Never throw, never affect user experience
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ Unexpected error in statistics tracking (silent):', error);
    }
    // Silently return - function completes without throwing
    return;
  }
};

